import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Home, Calendar, Banknote, FileText, Mail, Phone, Hash, ReceiptText, Wallet, AlertCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-blue-100 text-blue-800 border-blue-200",
  ended: "bg-gray-100 text-gray-700 border-gray-200",
  terminated: "bg-red-100 text-red-800 border-red-200",
};

export default function MyLease() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leases, setLeases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [{ data: ls }, { data: sm }] = await Promise.all([
        api.get("/my/lease"),
        api.get("/my/summary"),
      ]);
      setLeases(ls);
      setSummary(sm);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load your portal");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 fade-in" data-testid="my-lease-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          Welcome, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-[#525C58] mt-1">Your tenant portal — lease, invoices and payments.</p>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="my-summary-grid">
          <SummaryCard
            icon={ReceiptText}
            label="Total invoices"
            value={summary.invoice_count}
            tone="neutral"
            testid="summary-invoice-count"
          />
          <SummaryCard
            icon={Wallet}
            label="Paid to date"
            value={`KES ${Number(summary.total_paid_kes).toLocaleString()}`}
            tone="emerald"
            testid="summary-paid"
          />
          <SummaryCard
            icon={AlertCircle}
            label="Outstanding balance"
            value={`KES ${Number(summary.outstanding_kes).toLocaleString()}`}
            tone={summary.outstanding_kes > 0 ? "amber" : "emerald"}
            testid="summary-outstanding"
          />
          <SummaryCard
            icon={FileText}
            label="Unpaid invoices"
            value={summary.unpaid_count}
            tone={summary.unpaid_count > 0 ? "amber" : "emerald"}
            testid="summary-unpaid-count"
          />
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => navigate("/my/invoices")} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="quick-link-invoices">
          <ReceiptText className="h-4 w-4 mr-2" /> View my invoices <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        <Button onClick={() => navigate("/my/payments")} variant="outline" className="border-[#1B4332]/20 text-[#1B4332]" data-testid="quick-link-payments">
          <Wallet className="h-4 w-4 mr-2" /> Payment history
        </Button>
      </div>

      {/* Lease cards */}
      {loading ? (
        <p className="text-center text-[#7A8A84] py-10">Loading lease…</p>
      ) : leases.length === 0 ? (
        <Card className="border-[#E8E8E6]"><CardContent className="text-center py-10 text-[#7A8A84]">
          No active lease found on your account. Contact your hospital coordinator if this is wrong.
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {leases.map((ls) => (
            <Card key={ls.id} className="border-[#E8E8E6]" data-testid={`my-lease-${ls.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-2 text-lg">
                  <span className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-[#40916C]" /> {ls.unit_name || "Unit"}
                    {ls.unit_block && <span className="text-xs text-[#7A8A84]">· {ls.unit_block}</span>}
                  </span>
                  <Badge className={`${STATUS_COLOR[ls.status] || STATUS_COLOR.active} border text-xs`}>{ls.status}</Badge>
                </CardTitle>
                <CardDescription>{ls.tenant_name}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field icon={Calendar} label="Start date" value={ls.start_date} />
                  <Field icon={Calendar} label="End date" value={ls.end_date || "Open-ended (month-to-month)"} />
                  <Field icon={Banknote} label="Monthly rent" value={`${ls.rent_currency} ${Number(ls.monthly_rent).toLocaleString()}`} />
                  <Field icon={Hash} label="Billing day" value={`${ls.billing_day}${suffix(ls.billing_day)} of each month`} />
                  <Field icon={Mail} label="Email on file" value={ls.tenant_email || "—"} />
                  <Field icon={Phone} label="Phone" value={ls.tenant_phone || "—"} />
                </div>
                {ls.add_ons?.length > 0 && (
                  <div className="pt-3 border-t border-[#E8E8E6]">
                    <p className="text-xs font-semibold text-[#1B4332] mb-2">Recurring add-ons</p>
                    <div className="flex flex-wrap gap-2" data-testid={`addons-${ls.id}`}>
                      {ls.add_ons.map((a) => (
                        <span key={a.id} className="text-xs bg-[#F4F4F1] border border-[#E8E8E6] rounded-full px-3 py-1">
                          {a.label} — {a.currency} {Number(a.amount).toLocaleString()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {ls.deposit_amount > 0 && (
                  <p className="text-xs text-[#7A8A84]">
                    Deposit on file: <strong className="text-[#1B4332]">{ls.deposit_currency} {Number(ls.deposit_amount).toLocaleString()}</strong> ({ls.deposit_status})
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-[#7A8A84] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-[#A3ACA8] font-medium">{label}</p>
        <p className="text-sm text-[#1B4332] truncate">{value}</p>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    neutral: "bg-white border-[#E8E8E6] text-[#1B4332]",
  };
  return (
    <Card className={`${tones[tone] || tones.neutral} border`} data-testid={testid}>
      <CardContent className="p-4 flex items-start gap-3">
        <Icon className="h-5 w-5 mt-0.5 shrink-0 opacity-70" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide font-medium opacity-80">{label}</p>
          <p className="text-2xl font-semibold mt-0.5 truncate">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function suffix(n) {
  const num = Number(n);
  if (num >= 11 && num <= 13) return "th";
  switch (num % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}
